import { ActionReference, DefaultGameState, GameObjectReference, GameState, HTMLGameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1024px;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(35vh + 10px) minmax(calc(35vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
    }

    .title {
        text-align: center;
        margin-top: 10px;
        overflow: auto;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    .header img {
        width: auto;
        height: 100%;
        image-rendering: pixelated;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        margin-top: 10px;
        padding: 0 10px;
    }

    .content p {
        margin: 0 0 10px 0;
    }

    .content p:last-of-type {
        margin: 0;
    }

    .footer {
        border-radius: 10px 10px 0 0;
        background-color: #52478b;
        border: 1px solid #332c57;
        margin-top: 10px;
        display: flex;
        height: 105px;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        overflow: auto;
        padding: 10px 10px 0 10px;
    }

    .footer .button {
        background-color: #7f6ed7;
        border: 1px solid #332c57;
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: #332c57;
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    private _talkingToCharacterAlias?: string;

    private _showCharacterImage: boolean = false;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    private filterGameObjects(actionAlias: string): GameObjectReference[] {
        const objects: GameObjectReference[] = this._currentGameState?.objects || [];
        const roomName: string | undefined = this._currentGameState?.roomName;

        // Specifiek gedrag voor de Hotel Lobby
        if (roomName === "Hotel Lobby") {
            if (actionAlias === "examine") {
                return objects.filter(obj =>
                    obj.alias === "gastenboek" || obj.alias === "evidence-log" || obj.alias === "receptie-notities"
                );
            }

            if (actionAlias === "talk") {
                return objects.filter(obj => obj.alias === "receptionist");
            }
        }

        // Specifiek gedrag voor de Living Room
        if (roomName === "LivingRoom") {
            if (actionAlias === "examine") {
                return objects.filter(obj =>
                    obj.alias === "vent" || obj.alias === "bookshelf" || obj.alias === "cloth" || obj.alias === "evidence-log"
                );
            }

            if (actionAlias === "open") {
                return objects.filter(obj =>
                    obj.alias === "vent" || obj.alias === "bookshelf"
                );
            }

            if (actionAlias === "talk") {
                return objects.filter(obj =>
                    obj.alias === "isabella" || obj.alias === "oliver"
                );
            }
        }

        // Specifiek gedrag voor de storage Room
        if (roomName === "Storageroom") {
            if (actionAlias === "Analyze Locker") {
                return objects.filter(obj =>
                    obj.alias === "Open locker lock"
                );
            }

            if (actionAlias === "open") {
                return objects.filter(obj =>
                    obj.alias === "vent" || obj.alias === "bookshelf"
                );
            }

            if (actionAlias === "talk") {
                return objects.filter(obj =>
                    obj.alias === "isabella" || obj.alias === "oliver"
                );
            }
        }

        // Specifiek gedrag voor de Rooftop
        if (roomName === "Rooftop") {
            if (actionAlias === "examine") {
                return objects.filter(obj =>
                    obj.alias === "planter" || obj.alias === "evidence-log" || obj.alias === "shoe-prints"
                );
            }
            if (actionAlias === "analyze-footprints") {
                return objects.filter(obj =>
                    obj.alias === "shoe-prints"
                );
            }
        }

        // In alle andere gevallen: geef alles terug
        return objects;
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);
            return;
        }

        // Behandel het renderen van HTML componenten
        if (state.type === "html") {
            this.renderHTMLComponent(state);
            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render de HTML component op basis van de game state
     *
     * @param state HTML game state met componentnaam en eigenschappen
     */
    private renderHTMLComponent(state: HTMLGameState): void {
        if (!this.shadowRoot) {
            return;
        }

        console.log("Rendering HTML component:", state.componentName);

        // Verwijder de bestaande inhoud
        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }
        // de nieuwe stijl / inhoud toevoegen
        const styleElement: HTMLStyleElement = document.createElement("style");
        styleElement.textContent = styles; // zorg ervoor dat de stijl wordt toegevoegd
        this.shadowRoot.appendChild(styleElement);
        const container: HTMLDivElement = document.createElement("div"); // type annotation toevoegen
        container.style.width = "100%";
        container.style.maxWidth = "1024px";
        container.style.margin = "0 auto";
        container.style.padding = "20px";
        container.style.display = "flex";
        container.style.flexDirection = "column";
        container.style.alignItems = "center";
        container.style.minHeight = "80vh";
        container.style.background = "#121212";

        try {
            const title: HTMLHeadingElement = document.createElement("h2");
            // Set appropriate title based on component
            if (state.componentName === "planter-mini-game") {
                title.innerText = "Plant Watering";
            }
            else if (state.componentName === "cctv-analyzer") {
                title.innerText = "Security Terminal";
            }
            else if (state.componentName === "locker-lock-component") {
                title.innerText = "Locker Lock";
            }
            else {
                title.innerText = "";
            }
            title.style.color = "#fff";
            title.style.marginBottom = "20px";
            container.appendChild(title);
            const customElement: HTMLElement = document.createElement(state.componentName);
            container.appendChild(customElement);

            // Only add the return button for security terminal components, not for the planter mini-game
            if (state.componentName !== "planter-mini-game") {
                const backButton: HTMLButtonElement = document.createElement("button");
                backButton.innerText = "Return to Security Room";
                backButton.style.marginTop = "20px";
                backButton.style.padding = "10px 20px";
                backButton.style.backgroundColor = "#7f6ed7";
                backButton.style.border = "none";
                backButton.style.color = "#fff";
                backButton.style.cursor = "pointer";
                backButton.addEventListener("click", () => void this.refreshGameState());
                container.appendChild(backButton);
            }

            this.shadowRoot.appendChild(container);
        }
        catch (error: unknown) {
            console.error("Error rendering component:", error);
            const errorMsg: HTMLDivElement = document.createElement("div");
            errorMsg.textContent = "Failed to load component. Please try again.";
            errorMsg.style.color = "red";
            errorMsg.style.padding = "20px";
            container.appendChild(errorMsg);
            this.shadowRoot.appendChild(container);
        }
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (
            this._showCharacterImage &&
            roomName?.toLowerCase() === "hotel lobby" &&
            this._talkingToCharacterAlias === "receptionist"
        ) {
            return `
                <div class="header">
                    <img src="/assets/img/characters/receptionist.png" />
                </div>
            `;
        }

        const characterAlias: boolean | undefined = this._talkingToCharacterAlias === "receptionist";
        if (characterAlias) {
            return `
                <div class="header">
                    <img src="/assets/img/characters/receptionist.png" />
                </div>
            `;
        }

        // laat de standaard kamerafbeeldingen zien
        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header">
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                </div>
            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content">
                ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
            </div>
        `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div>
                        ${this._selectedActionButton
                            ? this.filterGameObjects(this._selectedActionButton.alias).map(button => this.renderGameObjectButton(button))
                            : ""
                        }
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        this._showCharacterImage = false;
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // als we een talkactie uitvoeren, sla het alias op
        if (this._selectedActionButton.alias === "talk") {
            this._talkingToCharacterAlias = button.alias;
        }

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        const alias: string = button.alias;

        // zet de afbeelding alleen op true als we praten met de receptionist
        if (this._selectedActionButton?.alias === "talk" && alias === "receptionist") {
            this._showCharacterImage = true;
        }
        else {
            this._showCharacterImage = false;
        }

        // Refresh the web component
        this.render();
        // Als de geselecteerde actie GEEN talk meer is, reset de character image
        if (this._selectedActionButton?.alias !== "talk") {
            this._talkingToCharacterAlias = undefined;
        }

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
